'use client';

import React, { useEffect, useState, useRef } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { useAnalytics } from '@/hooks/useAnalytics';

// Custom cursor component
const CustomCursor = () => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isPointer, setIsPointer] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    // Only run client-side code after mounting
    setIsMounted(true);
    
    if (typeof window !== 'undefined') {
      const updatePosition = (e: MouseEvent) => {
        setPosition({ x: e.clientX, y: e.clientY });
      };

      const updateCursorType = () => {
        const hoveredElement = document.elementFromPoint(position.x, position.y);
        setIsPointer(
          hoveredElement?.tagName === 'BUTTON' || 
          hoveredElement?.tagName === 'A' || 
          hoveredElement?.closest('button') !== null || 
          hoveredElement?.closest('a') !== null
        );
      };

      const handleMouseEnter = () => setIsVisible(true);
      const handleMouseLeave = () => setIsVisible(false);

      window.addEventListener('mousemove', updatePosition);
      window.addEventListener('mousemove', updateCursorType);
      document.addEventListener('mouseenter', handleMouseEnter);
      document.addEventListener('mouseleave', handleMouseLeave);

      return () => {
        window.removeEventListener('mousemove', updatePosition);
        window.removeEventListener('mousemove', updateCursorType);
        document.removeEventListener('mouseenter', handleMouseEnter);
        document.removeEventListener('mouseleave', handleMouseLeave);
      };
    }
  }, [position.x, position.y, isMounted]);

  // Don't render anything on the server or before mounting
  if (!isMounted) return null;

  return (
    <motion.div
      className="fixed top-0 left-0 z-50 pointer-events-none mix-blend-difference"
      animate={{
        x: position.x - 8,
        y: position.y - 8,
        scale: isPointer ? 1.5 : 1,
        opacity: isVisible ? 1 : 0,
      }}
      transition={{
        type: "spring",
        damping: 20,
        stiffness: 300,
        mass: 0.5,
      }}
    >
      <div className="w-4 h-4 rounded-full bg-white" />
    </motion.div>
  );
};

// Animated text reveal component
const AnimatedText = ({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) => {
  return (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ 
        duration: 0.8, 
        delay, 
        ease: [0.25, 0.1, 0.25, 1]
      }}
    >
      {children}
    </motion.div>
  );
};

export default function Hero() {
  const { trackClick } = useAnalytics();
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"]
  });
  
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 1.1]);
  const textY = useTransform(scrollYProgress, [0, 0.5], [0, -100]);
  
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    // Only run client-side code after mounting
    setIsMounted(true);
  }, []);

  const handleDemoClick = () => {
    trackClick('hero_demo_button', 'homepage_hero');
  };

  const handleExploreClick = () => {
    trackClick('hero_explore_features', 'homepage_hero');
    // Only run client-side code after mounting
    if (isMounted && typeof document !== 'undefined') {
      document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <motion.div 
      ref={ref}
      className="relative h-screen w-full overflow-hidden bg-futuristic-black"
      style={{ opacity, scale }}
    >
      {/* Custom cursor - only render on client side */}
      {isMounted && <CustomCursor />}
      
      {/* Video background with overlay */}
      <div className="absolute inset-0 z-0">
        {/* Sports video background - only render on client side */}
        {isMounted && (
          <video
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 w-full h-full object-cover opacity-10"
            onLoadedData={() => setVideoLoaded(true)}
          >
            <source src="https://assets.mixkit.co/videos/preview/mixkit-aerial-view-of-a-soccer-game-at-night-4821-large.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        )}
        
        {/* Gradient overlays */}
        <div className="absolute inset-0 bg-gradient-to-r from-futuristic-blue/20 via-futuristic-purple/10 to-black/80 z-10 opacity-90" />
        
        <div className="absolute inset-0 bg-futuristic-black opacity-80 z-0">
          {/* Background gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-futuristic-blue/10 via-futuristic-purple/5 to-futuristic-black opacity-90" />
        </div>
      </div>

      {/* Animated particles - only render on client side */}
      <div className="absolute inset-0 z-0">
        {isMounted && (
          <>
            <motion.div
              className="absolute top-1/4 left-1/4 w-1 h-1 rounded-full bg-white opacity-80"
              animate={{
                y: [0, -100, 0],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                repeatType: "loop",
              }}
            />
            <motion.div
              className="absolute top-1/3 left-2/3 w-2 h-2 rounded-full bg-futuristic-blue opacity-60"
              animate={{
                y: [0, -150, 0],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 10,
                repeat: Infinity,
                repeatType: "loop",
                delay: 1,
              }}
            />
            <motion.div
              className="absolute top-2/3 left-1/3 w-1.5 h-1.5 rounded-full bg-futuristic-purple opacity-70"
              animate={{
                y: [0, -120, 0],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 9,
                repeat: Infinity,
                repeatType: "loop",
                delay: 2,
              }}
            />
            <motion.div
              className="absolute bottom-1/3 right-1/4 w-2 h-2 rounded-full bg-futuristic-neon opacity-50"
              animate={{
                y: [0, -80, 0],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 7,
                repeat: Infinity,
                repeatType: "loop",
                delay: 3,
              }}
            />
          </>
        )}
      </div>

      {/* Glowing orbs */}
      <div className="absolute top-0 left-1/4 w-1/2 h-1/2 bg-futuristic-blue/5 rounded-full blur-[100px] -translate-y-1/2" />
      <div className="absolute bottom-0 right-1/4 w-1/2 h-1/2 bg-futuristic-purple/5 rounded-full blur-[100px] translate-y-1/2" />

      {/* Hero content */}
      <div className="relative z-10 h-full flex flex-col justify-center">
        <motion.div 
          className="container mx-auto px-4 sm:px-6 lg:px-8"
          style={{ y: textY }}
        >
          <div className="max-w-5xl mx-auto">
            {/* Headline */}
            <div className="overflow-hidden">
              <AnimatedText delay={0.2}>
                <h1 className="text-5xl md:text-7xl lg:text-8xl xl:text-9xl font-bold tracking-tight text-white uppercase">
                  <span className="block">Dominate</span>
                </h1>
              </AnimatedText>
            </div>
            
            <div className="overflow-hidden mt-2">
              <AnimatedText delay={0.4}>
                <h1 className="text-5xl md:text-7xl lg:text-8xl xl:text-9xl font-bold tracking-tight">
                  <span className="bg-gradient-to-r from-futuristic-neon via-futuristic-blue to-futuristic-purple bg-clip-text text-transparent uppercase">
                    The Business of Sports
                  </span>
                </h1>
              </AnimatedText>
            </div>

            {/* Subheadline */}
            <div className="overflow-hidden mt-8">
              <AnimatedText delay={0.6}>
                <p className="text-xl md:text-2xl text-gray-300 max-w-3xl">
                  The ultimate sports management platform that gives coaches, teams, and parents everything they need in one powerful ecosystem.
                </p>
              </AnimatedText>
            </div>
            
            {/* CTA Buttons */}
            <div className="mt-12">
              <AnimatedText delay={0.8}>
                <div className="flex flex-col sm:flex-row gap-6">
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    transition={{ type: "spring", stiffness: 400, damping: 10 }}
                  >
                    <Button
                      variant="neon"
                      size="xl"
                      href="/demo"
                      trackingName="hero_demo_button"
                      trackingLocation="homepage_hero"
                      className="text-lg px-12 py-7 rounded-full shadow-neon-glow hover:shadow-[0_0_40px_rgba(0,255,102,0.7)] transition-all duration-300"
                      onClick={handleDemoClick}
                    >
                      Get Started
                    </Button>
                  </motion.div>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    transition={{ type: "spring", stiffness: 400, damping: 10 }}
                  >
                    <Button
                      variant="futuristic"
                      size="xl"
                      trackingName="hero_explore_features"
                      trackingLocation="homepage_hero"
                      className="text-lg px-12 py-7 rounded-full border-2 border-white/10 text-white hover:border-white/20 hover:bg-white/5 transition-all duration-300 relative overflow-hidden group"
                      onClick={handleExploreClick}
                    >
                      <span className="relative z-10">Explore Features</span>
                      <motion.div 
                        className="absolute inset-0 -z-0 bg-gradient-to-r from-futuristic-blue via-futuristic-purple to-futuristic-neon opacity-0 group-hover:opacity-20 transition-opacity duration-300"
                        initial={false}
                        animate={{ opacity: 0 }}
                        whileHover={{ opacity: 0.2 }}
                        transition={{ duration: 0.3 }}
                      />
                    </Button>
                  </motion.div>
                </div>
              </AnimatedText>
            </div>
            
            {/* Stats */}
            <div className="mt-20">
              <AnimatedText delay={1}>
                <div className="grid grid-cols-3 gap-8 border-t border-white/10 pt-8">
                  <div>
                    <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">11K+</div>
                    <div className="text-sm text-gray-400 mt-1">Users</div>
                  </div>
                  <div>
                    <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">45+</div>
                    <div className="text-sm text-gray-400 mt-1">Organizations</div>
                  </div>
                  <div>
                    <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">50+</div>
                    <div className="text-sm text-gray-400 mt-1">Teams</div>
                  </div>
                </div>
              </AnimatedText>
            </div>
          </div>
        </motion.div>
        
        {/* Scroll indicator */}
        <motion.div 
          className="absolute bottom-10 left-1/2 transform -translate-x-1/2 flex flex-col items-center"
          animate={{ 
            y: [0, 10, 0],
            opacity: [0.5, 1, 0.5]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            repeatType: "loop"
          }}
        >
          <div className="text-white/50 text-sm uppercase tracking-widest mb-2">Scroll</div>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 5L12 19M12 19L19 12M12 19L5 12" stroke="white" strokeOpacity="0.5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </motion.div>
      </div>
    </motion.div>
  );
} 